<script setup lang="ts">
/**
 * The first checkout's form (os#1615).
 *
 * It asks for the name to invoice, the address, the country (from `countries`) and, only for a country in
 * `taxIdCountries`, a tax ID, which stays optional: plans are sold to people and businesses alike, and a business under
 * its country's threshold has none. Nothing is ticked: the product terms were accepted at sign-up. Submit emits
 * `submit` with the details.
 *
 * It renders no card input: the card is entered in FloPay's own fields (`BillingCardFields`), mounted inside the
 * payment container from the one adapter that reaches `@flopay/vue` (`lib/flopayFields.ts`), and the container is
 * kept out of session replay.
 */
import {
    Button,
    Input,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Textarea,
} from '@zerohuman/ui'
import { computed, ref } from 'vue'
import BillingCardFields from '@/components/BillingCardFields.vue'

export type CheckoutCountry = { code: string; name: string }

export type CheckoutDetails = {
    legalName: string
    address: string
    country: string
    taxId?: string
}

const props = defineProps<{
    countries: CheckoutCountry[]
    /** The country codes that have a business tax ID, which is when the tax ID field is shown. */
    taxIdCountries: string[]
    /** A FloPay field session for this enterprise's customer: FloPay's card fields are drawn once there is one. */
    fieldSession?: string | null
    /** While the checkout is being sent. */
    busy?: boolean
}>()

const emit = defineEmits<{
    submit: [details: CheckoutDetails]
    /** FloPay's fields saved a card: its payment method id, the only thing about it the portal ever holds. */
    paymentMethod: [paymentMethodId: string]
}>()

const legalName = ref('')
const address = ref('')
const country = ref('')
const taxId = ref('')

const hasTaxId = computed(() => props.taxIdCountries.includes(country.value))

function submit(): void {
    if (props.busy) {
        return
    }
    const tax = hasTaxId.value ? taxId.value.trim() : ''
    emit('submit', {
        legalName: legalName.value.trim(),
        address: address.value.trim(),
        country: country.value,
        ...(tax ? { taxId: tax } : {}),
    })
}
</script>

<template>
    <form class="grid gap-4" @submit.prevent="submit">
        <div class="grid gap-2">
            <Label for="billing-legal-name">Name to invoice</Label>
            <Input id="billing-legal-name" v-model="legalName" name="legalName" autocomplete="organization" required />
        </div>
        <div class="grid gap-2">
            <Label for="billing-address">Billing address</Label>
            <Textarea id="billing-address" v-model="address" name="address" autocomplete="street-address" required />
        </div>
        <div class="grid gap-2">
            <Label id="billing-country-label">Country</Label>
            <Select v-model="country">
                <SelectTrigger class="w-full" aria-labelledby="billing-country-label">
                    <SelectValue placeholder="Choose a country" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem v-for="option in countries" :key="option.code" :value="option.code">
                        {{ option.name }}
                    </SelectItem>
                </SelectContent>
            </Select>
        </div>
        <div v-if="hasTaxId" class="grid gap-2">
            <Label for="billing-tax-id">Tax ID (optional)</Label>
            <Input id="billing-tax-id" v-model="taxId" name="taxId" autocomplete="off" />
        </div>
        <BillingCardFields :field-session="fieldSession ?? null" @payment-method="emit('paymentMethod', $event)" />
        <Button type="submit" :disabled="busy">Subscribe</Button>
    </form>
</template>
